import type { MouseEvent, KeyboardEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Textfield, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { useDeleteTopicModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/hooks/useDeleteTopicModal";
import type { IModifiablePrompt } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/types";
import { MAX_TOPIC_NAME_LENGTH } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import { allTrackers } from "app/services/track/track";
import { StyledIconButton } from "pro-features/components/Rule/src/styledComponents";

import { TopicName } from "./TopicsTable.styled";

interface ITopicCardProps {
    topicName: string;
    allTopicNames: string[];
    isSelected: boolean;
    onEditApprove: (topicName: string, newName?: string) => void;
    onCancelEdit?: () => void;
    onSelection?: (topicName: string) => void;
    onDelete?: (topicName: string) => void;
    prompts?: IModifiablePrompt[];
    canBeEdited?: boolean;
}

export const TopicCard = ({
    topicName,
    allTopicNames,
    prompts,
    isSelected,
    onEditApprove,
    onCancelEdit,
    onDelete,
    onSelection,
    canBeEdited = true,
}: ITopicCardProps) => {
    const [editMode, setEditMode] = useState<boolean>(!topicName && canBeEdited);
    const [isHovered, setHovered] = useState<boolean>(false);
    const [newTopicName, setNewTopicName] = useState<string>("");
    const cardRef = useRef<HTMLDivElement>(null);
    const fieldErrorMessage = useMemo<string>(() => {
        if (newTopicName.includes(",")) {
            return translate("llm.setup.modal.topics.error.comma");
        }

        if (newTopicName.includes('"')) {
            return translate("llm.setup.modal.topics.error.invalid_characters");
        }

        if (newTopicName === topicName) {
            return "";
        }

        // Exclude the topic being renamed from the duplicate check; otherwise a
        // casing-only rename (e.g. "tf_Tech / Telco_Ai" -> "tf_Tech / Telco_AI")
        // is flagged as a duplicate of itself once both sides are lowercased.
        const newTopicNameLowercase = newTopicName.toLowerCase();
        const isDuplicate = allTopicNames?.some(
            (topic) => topic !== topicName && topic.toLowerCase() === newTopicNameLowercase,
        );
        return isDuplicate ? translate("llm.setup.modal.topics.error.duplicate") : "";
    }, [allTopicNames, newTopicName, topicName]);

    const hasFieldError = !!fieldErrorMessage;

    const displayButtons = canBeEdited && (isHovered || editMode);
    const promptsCount = prompts?.length ?? 0;
    const isNewTopic = !topicName;
    const cardPadding = useMemo<string>(() => {
        if (editMode) {
            return "8px 16px";
        } else if (displayButtons) {
            return "8px 16px 8px 24px";
        } else {
            return "16px 24px";
        }
    }, [editMode, displayButtons]);

    const { openModal: openDeletionModal, modal: deletionModal } = useDeleteTopicModal(() => onDelete?.(topicName));

    const onHoverButtonClick = (ev: MouseEvent, callback: () => void): void => {
        ev.preventDefault();
        ev.stopPropagation();
        callback?.();
    };

    const onEditApproveClick = useCallback((): void => {
        setEditMode(false);

        if (isNewTopic) {
            //create topic
            onEditApprove(newTopicName);
        } else {
            //rename topic
            onEditApprove(topicName, newTopicName);
            allTrackers.dispatchTrackEvent("Edit Campaign", "click", "edit topic/submit");
        }
    }, [isNewTopic, newTopicName, onEditApprove, topicName]);

    const onCancelEditClick = useCallback((): void => {
        setEditMode(false);
        onCancelEdit?.();

        if (!isNewTopic) {
            allTrackers.dispatchTrackEvent("Edit Campaign", "click", "edit topic/cancel");
        }
    }, [isNewTopic, setEditMode, onCancelEdit]);

    const onEditClick = (): void => {
        setEditMode(true);
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "edit topic/open");
    };

    const onDeleteClick = (): void => {
        setEditMode(false);
        openDeletionModal(topicName);
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "delete topic/open");
    };

    const onKeyDown = (ev: KeyboardEvent<HTMLInputElement>): void => {
        if (ev.key === "Enter" && editMode && !!newTopicName && !hasFieldError) {
            onEditApproveClick();
        }
    };

    useEffect(() => {
        const handleClickOutside = (ev: Event): void => {
            if (editMode && cardRef.current && !cardRef.current.contains(ev.target as Node)) {
                if (newTopicName && newTopicName !== topicName && !hasFieldError) {
                    onEditApproveClick();
                } else {
                    onCancelEditClick();
                }
            }
        };

        if (editMode) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [editMode, setEditMode, topicName, newTopicName, onEditApproveClick, onCancelEditClick, hasFieldError]);

    return (
        <>
            <Flex
                ref={cardRef}
                alignItems="center"
                justifyContent="space-between"
                padding={cardPadding}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                background={colorsPalettes.carbon[displayButtons || isSelected ? 25 : 0]}
                onClick={() => onSelection?.(topicName)}
                borderBottom={`1px solid ${colorsPalettes.carbon[100]}`}
                cursor="pointer"
                gap="20px"
            >
                {editMode ? (
                    <Textfield
                        dataAutomation="topic-card-name-field"
                        onChange={(value) => setNewTopicName(value)}
                        placeholder={topicName}
                        defaultValue={topicName}
                        onKeyDown={onKeyDown}
                        error={hasFieldError}
                        errorMessage={fieldErrorMessage}
                        maxLength={MAX_TOPIC_NAME_LENGTH}
                        height={32}
                        autoFocus
                    />
                ) : (
                    <TopicName
                        fontVariant="input2"
                        color={colorsPalettes.carbon[isSelected ? 500 : 300]}
                    >
                        {topicName}
                    </TopicName>
                )}
                {displayButtons ? (
                    <Flex gap="4px">
                        {editMode ? (
                            //create or rename topic
                            <IconButton
                                type="flat"
                                iconName="checked"
                                iconSize="xs"
                                isDisabled={!newTopicName || hasFieldError}
                                onClick={(ev: MouseEvent) => onHoverButtonClick(ev, onEditApproveClick)}
                            />
                        ) : (
                            //start editing
                            <Tooltip content={translate("common.rename")}>
                                <StyledIconButton
                                    type="flat"
                                    iconName="edit-icon"
                                    iconSize="xs"
                                    onClick={(ev: MouseEvent) => onHoverButtonClick(ev, onEditClick)}
                                />
                            </Tooltip>
                        )}
                        {editMode ? (
                            //cancel editing
                            <IconButton
                                type="flat"
                                iconName="close"
                                iconSize="xs"
                                onClick={(ev: MouseEvent) => onHoverButtonClick(ev, onCancelEditClick)}
                            />
                        ) : (
                            //delete topic
                            <Tooltip content={translate("global.delete")}>
                                <IconButton
                                    type="flat"
                                    iconName="delete"
                                    iconSize="xs"
                                    isDisabled={!onDelete}
                                    onClick={(ev: MouseEvent) => onHoverButtonClick(ev, onDeleteClick)}
                                />
                            </Tooltip>
                        )}
                    </Flex>
                ) : (
                    <Text
                        fontVariant="input2"
                        color={colorsPalettes.carbon[isSelected ? 500 : 300]}
                    >
                        {promptsCount}
                    </Text>
                )}
            </Flex>
            {deletionModal}
        </>
    );
};
